import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Save, Eye, Globe, Upload, Plus, Trash2,
  GripVertical, Image, Video, Music, Calendar, Mail,
  User, Layout, Palette, Settings, ExternalLink, Newspaper, Copy, Quote, Star,
  Briefcase, Users, HelpCircle, MessageSquare, Phone, CheckCircle2, XCircle, AlertTriangle, Link, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import CloudinaryUploader, { GalleryUploader } from '@/components/CloudinaryUploader';

export const PressModuleEditor = ({ data, onChange }) => {
  const quotes = data.quotes || [];
  
  const addQuote = () => {
    onChange({
      ...data, 
      quotes: [...quotes, { 
        id: Date.now().toString(),
        quote: '', 
        source: '', 
        publication: '',
        rating: 5
      }]
    });
  };
  
  const updateQuote = (idx, field, value) => {
    const updated = [...quotes];
    updated[idx] = {...updated[idx], [field]: value};
    onChange({...data, quotes: updated});
  };
  
  const removeQuote = (idx) => {
    onChange({...data, quotes: quotes.filter((_, i) => i !== idx)});
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Section Title</Label>
        <Input 
          value={data.title || 'Press'} 
          onChange={(e) => onChange({...data, title: e.target.value})}
        />
      </div>
      
      <div className="space-y-2">
        <Label>Subtitle</Label>
        <Input 
          value={data.subtitle || 'What the critics say'} 
          onChange={(e) => onChange({...data, subtitle: e.target.value})}
        />
      </div>

      <div className="flex items-center justify-between">
        <Label>Press Quotes</Label>
        <Button onClick={addQuote} size="sm" variant="outline" className="text-slate-300 border-slate-600">
          <Plus className="w-4 h-4 mr-1" />
          Add Quote
        </Button>
      </div>

      {quotes.length === 0 ? (
        <p className="text-slate-400 text-sm py-8 text-center border border-dashed border-slate-600 rounded-xl bg-slate-700/20">
          No press quotes yet. Add your first review!
        </p>
      ) : (
        <div className="space-y-4">
          {quotes.map((q, idx) => (
            <div key={q.id || idx} className="p-4 bg-slate-700/50 rounded-xl border border-slate-600 space-y-3">
              <div className="flex items-start justify-between">
                <span className="text-sm font-medium text-slate-300">Quote {idx + 1}</span>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => removeQuote(idx)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs">Quote Text</Label>
                <Textarea 
                  value={q.quote || ''}
                  onChange={(e) => updateQuote(idx, 'quote', e.target.value)}
                  placeholder='"An extraordinary performance..."'
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">Publication</Label>
                  <Input 
                    value={q.publication || ''}
                    onChange={(e) => updateQuote(idx, 'publication', e.target.value)}
                    placeholder="The Guardian"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Reviewer/Source</Label>
                  <Input 
                    value={q.source || ''}
                    onChange={(e) => updateQuote(idx, 'source', e.target.value)}
                    placeholder="Jane Smith"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs">Star Rating (1-5)</Label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => updateQuote(idx, 'rating', star)}
                      className={cn(
                        "w-8 h-8 rounded transition-colors",
                        star <= (q.rating || 5) 
                          ? "text-amber-400 hover:text-amber-300" 
                          : "text-slate-500 hover:text-slate-400"
                      )}
                    >
                      <Star className="w-6 h-6 fill-current" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Services Module Editor

